import { useState } from "react";
import { usePageHeader } from "@/hooks/usePageHeader";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Clock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useOvertimeSummary } from "@/hooks/useClockify";
import { useAllEmployeeAbsences } from "@/hooks/useEmployeeAbsences";
import { formatDate, countBusinessDays } from "@/lib/date-utils";

export default function HRPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const { data: overtimeData, isLoading: overtimeLoading } =
    useOvertimeSummary(year);
  const { data: allAbsences = [], isLoading: absencesLoading } =
    useAllEmployeeAbsences(year);

  const absencesByPerson = new Map<
    string,
    { name: string; vacation: number; sick: number; other: number }
  >();
  for (const h of allAbsences) {
    const person = (h as Record<string, unknown>).people as {
      first_name: string;
      last_name: string;
    } | null;
    const name = person
      ? `${person.first_name} ${person.last_name}`
      : "Unknown";
    const personId = h.person_id;
    const existing = absencesByPerson.get(personId) || {
      name,
      vacation: 0,
      sick: 0,
      other: 0,
    };
    const days = countBusinessDays(h.start_date, h.end_date);
    if (h.absence_type === "vacation") existing.vacation += days;
    else if (h.absence_type === "sick") existing.sick += days;
    else existing.other += days;
    absencesByPerson.set(personId, existing);
  }

  const personLink = (personId: string) =>
    `/people/${personId}?tab=absences-overtime&year=${year}`;

  usePageHeader({ title: "HR Overview" });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => setYear((y) => y - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-12 text-center">{year}</span>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => setYear((y) => y + 1)}
            disabled={year >= currentYear}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overtime Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Overtime Balance
            </CardTitle>
            <CardDescription>All employees for {year}</CardDescription>
          </CardHeader>
          <CardContent>
            {overtimeLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : !overtimeData?.employees?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No data available
              </p>
            ) : (
              <div className="space-y-1">
                <div className="flex text-xs text-muted-foreground font-medium border-b pb-1">
                  <span className="flex-1">Name</span>
                  <span className="w-20 text-right">Balance</span>
                  <span className="w-20 text-right">Actual</span>
                  <span className="w-24 text-right">Last Entry</span>
                </div>
                {overtimeData.employees.map((emp) => (
                  <Link
                    key={emp.person_id}
                    to={personLink(emp.person_id)}
                    className="flex items-center py-1.5 px-1 rounded hover:bg-muted/50 text-sm"
                  >
                    <span className="flex-1 truncate">{emp.name}</span>
                    <span
                      className={`w-20 text-right font-mono text-xs ${emp.balance >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                    >
                      {emp.balance >= 0 ? "+" : ""}
                      {emp.balance.toFixed(1)}h
                    </span>
                    <span className="w-20 text-right font-mono text-xs">
                      {emp.actual_hours.toFixed(1)}h
                    </span>
                    <span className="w-24 text-right text-xs text-muted-foreground">
                      {emp.last_entry
                        ? formatDate(emp.last_entry.slice(0, 10))
                        : "—"}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Absence Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Absence Overview
            </CardTitle>
            <CardDescription>All employees for {year}</CardDescription>
          </CardHeader>
          <CardContent>
            {absencesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : absencesByPerson.size === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No absences recorded
              </p>
            ) : (
              <div className="space-y-1">
                <div className="flex text-xs text-muted-foreground font-medium border-b pb-1">
                  <span className="flex-1">Name</span>
                  <span className="w-20 text-right">Vacation</span>
                  <span className="w-16 text-right">Sick</span>
                  <span className="w-16 text-right">Other</span>
                </div>
                {Array.from(absencesByPerson.entries())
                  .sort((a, b) => a[1].name.localeCompare(b[1].name))
                  .map(([personId, data]) => (
                    <Link
                      key={personId}
                      to={personLink(personId)}
                      className="flex items-center py-1.5 px-1 rounded hover:bg-muted/50 text-sm"
                    >
                      <span className="flex-1 truncate">{data.name}</span>
                      <span className="w-20 text-right font-mono text-xs">
                        {data.vacation}d
                      </span>
                      <span className="w-16 text-right font-mono text-xs">
                        {data.sick}d
                      </span>
                      <span className="w-16 text-right font-mono text-xs">
                        {data.other}d
                      </span>
                    </Link>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
